<template>
  <div class="grading-reports md-scrollbar md-theme-default">
    <div class="table-container">
      <section-title v-if="assignment" color="#0870B0" class="assignment-title">
        {{ assignment.name }}
        <template v-slot:subtitle>
          Scenario Name: {{ caseConf && caseConf.name }}
        </template>
      </section-title>
      <toolbar
        v-if="groupByUser"
        :title="assignment ? 'Assigned students' : caseConf && caseConf.name"
        @search="searchGrades"
      />
      <toolbar
        v-if="groupByCase"
        :title="userLabel(user)"
        @search="searchGrades"
      />
      <md-progress-bar v-if="loading" md-mode="indeterminate" />
      <md-table
        class="grading-reports-table"
        :value="sortedReports"
        md-card
        :md-sort.sync="state.sortField"
        :md-sort-order.sync="state.sortOrder"
        @md-selected="onSelect"
      >
        <md-table-row
          slot="md-table-row"
          slot-scope="{ item: gradingReport }"
          md-selectable="single"
        >
          <md-table-cell v-if="groupByUser" md-label="User Name" md-sort-by="name">
            {{ userLabel(gradingReport.user) }}
          </md-table-cell>
          <md-table-cell v-if="groupByCase" md-label="Scenario Name" md-sort-by="caseName">
            {{ gradingReport.caseData ? gradingReport.caseData.name : '[Scenario Unavailable]' }}
          </md-table-cell>
          <md-table-cell v-if="assignment" md-label="Groups">
            {{
              gradingReport.throughGroup
                ? groupLabel(gradingReport.throughGroup)
                : ''
            }}
          </md-table-cell>
          <md-table-cell md-label="Grade" md-sort-by="grade">
            <span v-if="!gradingReport.isStub">{{
              percentage(gradingReport)
            }}</span>
          </md-table-cell>
          <md-table-cell
            md-label="Critical Actions"
            md-sort-by="criticalActions"
          >
            <span v-if="!gradingReport.isStub">{{ successfulCriticalActions(gradingReport) }} out of
              {{ totalCriticalActions(gradingReport) }}</span>
          </md-table-cell>
          <md-table-cell md-label="Success/Fail" md-sort-by="successful">
            <span v-if="!gradingReport.isStub">{{
              `${gradingReport.successful ? 'Success' : 'Fail' }${gradingReport.exitEarly ? ' - Incomplete' : ''}`
            }}</span>
          </md-table-cell>
          <md-table-cell
            v-if="!assignment"
            md-label="Last Attempt"
            md-sort-by="attemptDate"
          >
            <span v-if="!gradingReport.isStub">{{
              formatDate(gradingReport.attemptDate)
            }}</span>
          </md-table-cell>
          <md-table-cell
            v-if="assignment"
            md-label="Attempts"
            md-sort-by="attemptsCount"
          >
            <span>{{ consumedAttemptsByUserId[gradingReport.user.user_id] || 0 }}/{{
              attemptsCount(gradingReport.user)
            }}</span>
          </md-table-cell>
          <md-table-cell
            v-if="assignment"
            md-label="Add Attempts"
            class="add-attempts-table-cell"
          >
            <span>
              <md-button
                class="md-icon-button"
                @click.stop="addAttempts(gradingReport.user)"
              >
                <md-icon>add</md-icon>
              </md-button>
            </span>
          </md-table-cell>
        </md-table-row>
      </md-table>
      <md-empty-state
        v-if="!loading && !reports.length && !error"
        md-icon="devices_other"
        md-label="Nothing to show"
        md-description="No grades to show for this case"
      />
      <md-empty-state
        v-if="!loading && error"
        class="md-accent"
        md-icon="error"
        md-label="An error occurred"
        :md-description="error.message || 'Unknown error'"
      />
      <md-toolbar class="md-primary bottom-toolbar empty-toolbar" />
    </div>
    <div class="details-container">
      <grading-report-summary
        v-if="state.selectedReport"
        :report="state.selectedReport"
        :case-conf="caseConf"
        :assignment="assignment"
        :user="user"
        :token="idToken"
      />
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import sortBy from 'lodash/sortBy';
import reverse from 'lodash/reverse';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import toArray from 'lodash/toArray';
import mapValues from 'lodash/mapValues';
import sumBy from 'lodash/sumBy';

import toLower from 'lodash/toLower';
import Fuse from 'fuse.js';

import Toolbar from '~lib/common/components/Toolbar';
import { userLabel, groupLabel } from '~lib/common/utils/labels';

import SectionTitle from '../../common/SectionTitle';
import GradingReportSummary from '../GradingReportSummary';

export default {
  name: 'grading-reports',
  components: {
    Toolbar,
    SectionTitle,
    GradingReportSummary,
  },
  props: {
    caseConf: {
      type: Object,
      default: () => null,
    },
    assignment: {
      type: Object,
      default: () => null,
    },
    user: {
      type: Object,
      default: () => null,
    },
    reports: {
      type: Array,
      default: () => [],
    },
    attempts: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
    },
    error: {
      type: Error,
      default: () => null,
    },
    idToken: {
      type: String,
      required: true,
    },
    groupByCase: {
      type: Boolean,
      default: false,
    },
    groupByUser: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      state: {
        selectedReport: null,
        sortField: 'grade',
        sortOrder: 'asc',
        searchTerm: '',
      },
      fuse: null,
    };
  },
  computed: {
    groupedReports() {
      return toArray(groupBy(this.reports, this.groupByCase ? 'caseId' : 'userId')).map(group =>
        reverse(sortBy(group, 'attemptDate'))
      );
    },
    latestReports() {
      return this.groupedReports.map(group => ({
        ...group[0],
        history: group.filter(report => !report.isStub),
      }));
    },
    filteredReports() {
      if (!this.state.searchTerm) {
        return this.latestReports;
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.fuse =
        this.fuse ||
        new Fuse(this.latestReports, {
          keys: ['user.given_name', 'user.family_name'],
          distance: 1,
          threshold: 0.4,
        });
      return this.fuse.search(this.state.searchTerm);
    },
    sortedReports() {
      const { sortField, sortOrder } = this.state;
      const sorted = sortBy(this.filteredReports, report => {
        if (sortField === 'grade') {
          return this.percentage(report);
        }
        if (sortField === 'name') {
          return toLower(
            get(report.user, 'given_name') + get(report.user, 'family_name')
          );
        }
        if (sortField === 'caseName') {
          return toLower(
            get(report, 'caseData.name')
          );
        }
        if (sortField === 'criticalActions') {
          return this.successfulCriticalActions(report);
        }
        if (sortField === 'attemptDate') {
          return report.attemptDate;
        }
        if (sortField === 'attemptsCount') {
          return report.history ? report.history.length : 0;
        }
        return this.percentage(report);
      });
      if (sortOrder === 'desc') {
        return reverse(sorted);
      }
      return sorted;
    },
    consumedAttemptsByUserId() {
      return mapValues(groupBy(this.attempts, 'userId'), userAttempts => userAttempts.length);
    },
    // Takes all the additional attempts and counts them
    // by principal type, then id (e.g. { USER: { user1: 3, user2: 1 } }, GROUP: { group1: 2 } )
    additionalAttemptsCountByPrincipalTypeAndId() {
      if (!this.assignment) {
        return {};
      }
      const groupedByType = groupBy(
        this.assignment.additionalAttempts,
        'principalType'
      );
      const countByTypeAndId = mapValues(groupedByType, principalTypeGroup => {
        const groupedById = groupBy(principalTypeGroup, 'principalId');
        return mapValues(groupedById, principalGroup =>
          sumBy(principalGroup, 'count')
        );
      });
      return countByTypeAndId;
    },
  },
  methods: {
    onSelect(report) {
      // md-table selected event triggers with null when clicked twice
      if (!report) {
        return;
      }
      if (report.isStub) {
        this.state.selectedReport = null;
        return;
      }
      this.state.selectedReport = report;
    },
    searchGrades(searchTerm) {
      this.state.searchTerm = searchTerm;
    },
    addAttempts(user) {
      this.$emit('addAssignmentAttempts', user);
    },
    attemptsCount(user) {
      const { assignment } = this;
      return (
        assignment.attemptsCount +
        get(
          this.additionalAttemptsCountByPrincipalTypeAndId,
          `USER.${user.user_id}`,
          0
        )
      );
    },
    formatDate(date) {
      return moment(date).format('MMMM D, YYYY h:mm A');
    },
    successfulCriticalActions(report) {
      if (report.isStub) {
        return null;
      }
      return [
        ...report.successfullyTakenActions,
        ...report.successfullyAvoidedActions,
      ].filter(action => action.isCritical).length;
    },
    totalCriticalActions(report) {
      if (report.isStub) {
        return null;
      }
      return [
        ...report.successfullyTakenActions,
        ...report.successfullyAvoidedActions,
        ...report.failedToTakeActions,
        ...report.failedToAvoidActions,
      ].filter(action => action.isCritical).length;
    },
    totalPoints(report) {
      if (report.isStub) {
        return null;
      }
      return [
        ...report.successfullyTakenActions,
        ...report.successfullyAvoidedActions,
        ...report.failedToTakeActions,
        ...report.failedToAvoidActions,
      ].reduce((sum, action) => sum + action.points, 0);
    },
    percentage(report) {
      if (report.isStub) {
        return null;
      }
      return Math.round((report.grade / this.totalPoints(report)) * 100);
    },
    userLabel,
    groupLabel,
  },
};
</script>

<style scoped>
.grading-reports {
  display: flex;
  width: 100%;
  padding: 20px 25px;
  overflow: auto;
}

.assignment-title {
  margin-bottom: 30px;
}

.table-container {
  width: 65%;
}

.details-container {
  width: 35%;
  height: 100%;
}

.md-table-row.md-selected-single {
  font-weight: normal;
  background-color: #d1e8f5 !important;
}

.md-empty-state {
  color: #000;
}

.add-attempts-table-cell {
  text-align: center;
}
</style>
